import React, { Component } from "react";
import { view } from "react-easy-state";
import testShader from "../shaders/testShader.js";
import glfunc from "../glfunc.js";
import store from "../state.js";
import hermite from "cubic-hermite-spline";

import BrushCursor from "./BrushCursor";
import Pointable from "react-pointable";

const vec2 = window.vec2;

class Canvas extends Component {
  state = {
    ctx: null,
    dragging: false,
    lastDrawPos: [0, 0, 0],
    cursorPosHistory: [[0, 0, 0]],
    force: 0
  };

  componentDidMount() {
    let gl = this.refs.canvas.getContext("webgl", {
      preserveDrawingBuffer: true
    });
    let buffer = gl.createBuffer();

    glfunc.createUnitQuad(gl);
    glfunc.createBrushProgram(gl, testShader);

    gl.clearColor(0, 0, 0, 1.0);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

    this.setState({ ctx: gl });
  }

  updateBrush(brush, pressure) {
    let thisBrush = Object.assign({}, store.brush);

    if (thisBrush.flowControl) {
      thisBrush.flow = thisBrush.flow * pressure;
    }
    if (thisBrush.sizeControl) {
      thisBrush.size = Math.max(thisBrush.size * pressure, 2);
    }
    return thisBrush;
  }

  handleDrawBegin = e => {
    e.preventDefault();
    let pressure = e.pointerType === "pen" ? e.pressure : 1;
    let thisBrush = this.updateBrush(store.brush, pressure);
    let pos = [this.props.position.x, this.props.position.y, pressure];

    glfunc.updateCanvas(
      this.state.ctx,
      pos[0],
      pos[1],
      thisBrush,
      store.palette[store.selectedIndex]
    );

    this.setState({
      dragging: e.type,
      lastDrawPos: pos,
      cursorPosHistory: [pos, pos, pos]
    });
  };

  handleDrawEnd = e => {
    this.handleDraw(e);
    this.setState({ dragging: false });
  };

  handleDrawEnter = e => {
    // stop drawing if we let go of the button while off the canvas
    if (e.buttons !== 1 && this.state.dragging)
      this.setState({ dragging: false });
  };

  handleDraw = e => {
    e.preventDefault();

    // Get pointer position in canvas space
    let clientRect = this.refs.canvas.getBoundingClientRect();
    let mousePosX = e.pageX - clientRect.left;
    let mousePosY = e.pageY - clientRect.top;
    let pressure = e.pointerType === "pen" ? e.pressure : 1;
    let thisPos = [mousePosX, mousePosY, pressure];

    // update current cursor position
    let newCursorPositions = [thisPos].concat(this.state.cursorPosHistory);
    if (newCursorPositions.length > 4) newCursorPositions.splice(-1);
    this.setState({ cursorPosHistory: newCursorPositions });

    if (this.state.dragging) {
      // get points and tangents for interpolation
      var points = [newCursorPositions[2], newCursorPositions[1]];
      var tangents = [
        this.getTangent(
          this.subtractVec(newCursorPositions[3], newCursorPositions[2]),
          this.subtractVec(newCursorPositions[2], newCursorPositions[1])
        ),
        this.getTangent(
          this.subtractVec(newCursorPositions[2], newCursorPositions[1]),
          this.subtractVec(newCursorPositions[1], newCursorPositions[0])
        )
      ];

      let length = vec2.length(
        this.subtractVec(newCursorPositions[2], newCursorPositions[1])
      );
      let steps = Math.min(
        Math.max(Math.floor(length / (store.brush.size / 5)), 1),
        Math.min(20, length)
      );

      for (var t = 0; t < steps; t += 1) {
        var point = hermite(t / steps, points, tangents);
        point[2] = this.lerp(
          newCursorPositions[2][2],
          newCursorPositions[1][2],
          t / steps
        );
        this.drawStroke(this.state.ctx, point);
      }
    }
  };

  drawStroke = (ctx, pos) => {
    let mousePosX = pos[0];
    let mousePosY = pos[1];
    let pressure = pos[2];

    // make copy of last position to operate on
    let lastDrawPos = this.state.lastDrawPos.slice();

    // Create new brush with default pressure of 1 if no pen detected
    let thisBrush = this.updateBrush(store.brush, pressure);

    // Get brush spacing in pixels based on current brush (min 1)
    let spacing =
      thisBrush.size * (thisBrush.roundness / 100) * (thisBrush.spacing / 100);
    if (spacing < 1) spacing = 1;

    // Calculate distance since last stroke position
    let distance = Math.hypot(
      mousePosX - lastDrawPos[0],
      mousePosY - lastDrawPos[1]
    );

    // If we've exceeded the minimum spacing, continue stroke
    if (distance > spacing) {
      // Get vector towards destination of length 'spacing'
      let deltaVector = [
        mousePosX - lastDrawPos[0],
        mousePosY - lastDrawPos[1]
      ];
      vec2.normalize(deltaVector, deltaVector);
      vec2.scale(deltaVector, deltaVector, spacing);

      let endPos = [mousePosX, mousePosY];
      let initialDistance = vec2.distance(lastDrawPos, endPos);
      let currentDistance = vec2.distance(lastDrawPos, endPos);

      // Draw stroke towards destingation until there's no more room left for spacing
      while (currentDistance > spacing) {
        // increment stroke
        vec2.add(lastDrawPos, lastDrawPos, deltaVector);

        // draw brush
        glfunc.updateCanvas(
          ctx,
          lastDrawPos[0],
          lastDrawPos[1],
          this.updateBrush(
            thisBrush,
            this.lerp(
              lastDrawPos[2],
              pressure,
              1 - currentDistance / initialDistance
            )
          ),
          store.palette[store.selectedIndex]
        );

        // recalculate current distance to endpos
        currentDistance = vec2.distance(lastDrawPos, endPos);
      }

      // Remember where we stopped the brush stroke for next time
      lastDrawPos[2] = pressure; // update new pressure
      this.setState({
        lastDrawPos: lastDrawPos
      });
    }
  };

  // linear lerp utility function
  lerp(start, end, amt) {
    return (1 - amt) * start + amt * end;
  }
  lerpVector2(start, end, amt) {
    return [
      this.lerp(start[0], end[0], amt), //
      this.lerp(start[1], end[1], amt)
    ];
  }
  lerpVector3(start, end, amt) {
    return [
      this.lerp(start[0], end[0], amt),
      this.lerp(start[1], end[1], amt),
      this.lerp(start[2], end[2], amt)
    ];
  }

  getTangent(v1, v2) {
    let tang1 = [0, 0];
    vec2.add(tang1, v1, v2);
    vec2.normalize(tang1, tang1);
    vec2.scale(tang1, tang1, Math.min(vec2.length(v1), vec2.length(v2)));
    return tang1;
  }

  subtractVec(v1, v2) {
    let vect = [0, 0];
    vec2.subtract(vect, v2, v1);
    return vect;
  }

  render() {
    return (
      <div
        style={{
          position: "relative",
          touchAction: "none",
          cursor: "none"
        }}
      >
        <Pointable
          onPointerDown={this.handleDrawBegin}
          onPointerMove={this.handleDraw}
          onPointerUp={this.handleDrawEnd}
          onPointerOver={this.handleDrawEnter}
        >
          <canvas
            ref="canvas"
            width={this.props.width}
            height={this.props.height}
            // onTouchStart={this.handleDrawBegin}
            // onTouchMove={this.handleDraw}
            // onTouchEnd={this.handleDrawEnd}
          />
        </Pointable>
        <BrushCursor
          cursorPos={
            this.state.cursorPosHistory[0]
              ? this.state.cursorPosHistory[0]
              : [0, 0, 0]
          }
          brush={store.brush}
          hardness
        />
      </div>
    );
  }
}

export default view(Canvas);
